import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Users, ChevronRight } from 'lucide-react';
import { OnlineIndicator } from './OnlineIndicator';

export const MobileChatDropdown = ({ isOpen, onClose }) => {
  const { api, user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && user) {
      fetchOnlineUsers();
    }
  }, [isOpen, user]);
  
  const fetchOnlineUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/online?limit=10');
      setOnlineUsers(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Error fetching online users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 md:hidden"
        onClick={onClose}
      />
      
      {/* Dropdown Panel */}
      <div 
        className="fixed top-16 right-0 left-0 mx-4 max-h-[70vh] bg-dark-paper border border-white/10 rounded-lg shadow-xl z-50 md:hidden overflow-hidden"
        data-testid="mobile-chat-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10 bg-dark">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-gold" />
            <span className="text-white font-medium text-sm">En ligne</span>
            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
              {onlineUsers.length}
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Users List */}
        <div className="overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            </div>
          ) : onlineUsers.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              Aucun membre en ligne
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {onlineUsers.map((onlineUser) => (
                <Link
                  key={onlineUser.id}
                  to={`/chat/${onlineUser.id}`}
                  onClick={onClose}
                  className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-dark-subtle flex items-center justify-center overflow-hidden">
                      {onlineUser.profile_picture ? (
                        <img 
                          src={getImageUrl(onlineUser.profile_picture)}
                          alt={onlineUser.pseudo}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gold">{onlineUser.pseudo?.[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <OnlineIndicator isOnline={true} className="absolute -bottom-0.5 -right-0.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{onlineUser.pseudo}</p>
                    <p className="text-gray-500 text-xs">
                      {onlineUser.profile_type === 'couple' ? 'Couple' : onlineUser.gender === 'male' ? 'Homme' : 'Femme'}
                      {onlineUser.age && ` • ${onlineUser.age} ans`}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </Link>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-3 border-t border-white/10 bg-dark">
          <Link
            to="/messages"
            onClick={onClose}
            className="block w-full text-center py-2 bg-gold/10 text-gold text-sm font-medium rounded hover:bg-gold/20 transition-colors"
          >
            Voir toutes les conversations
          </Link>
        </div>
      </div>
    </>
  );
};

export default MobileChatDropdown;
